import axios, { AxiosError } from 'axios'

export interface ApiErrorResponse {
  statusCode: number
  errorCode: number
  message: string
  errors?: Record<string, string[]> | null
  traceId?: string | null
  error?: string
}

export interface NormalizedApiError {
  statusCode?: number
  errorCode?: number
  message: string
  errors?: Record<string, string[]>
  details?: unknown
  traceId?: string
  isNetworkError: boolean
  isCancelled: boolean
  method?: string
  url?: string
  originalError: unknown
}

interface GraphQLErrorLike {
  message?: unknown
  extensions?: unknown
}

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again later.'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const getString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined

const getNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined

const getValidationErrors = (
  value: unknown,
): Record<string, string[]> | undefined => {
  if (!isRecord(value)) return undefined

  const entries = Object.entries(value).flatMap(([field, messages]) => {
    if (!Array.isArray(messages)) return []
    const validMessages = messages
      .map(getString)
      .filter((message): message is string => Boolean(message))
    return validMessages.length ? [[field, validMessages] as const] : []
  })

  return entries.length ? Object.fromEntries(entries) : undefined
}

const parseJsonRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (isRecord(value)) return value
  if (typeof value !== 'string') return undefined

  try {
    const parsed: unknown = JSON.parse(value)
    return isRecord(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

const getAxiosDetails = (error: unknown) => {
  if (!axios.isAxiosError(error)) return undefined

  const axiosError = error as AxiosError
  const data = axiosError.response?.data
  const body = parseJsonRecord(data)
  const responseStatus = axiosError.response?.status

  return {
    body,
    stringBody: getString(data),
    statusCode:
      getNumber(body?.statusCode) ??
      getNumber(body?.StatusCode) ??
      responseStatus,
    errorCode: getNumber(body?.errorCode) ?? getNumber(body?.ErrorCode),
    traceId: getString(body?.traceId) ?? getString(body?.TraceId),
    errors: getValidationErrors(body?.errors ?? body?.Errors),
    details: body?.details ?? body?.Details,
    message:
      getString(body?.message) ??
      getString(body?.Message) ??
      getString(body?.title) ??
      getString(body?.error) ??
      getString(data) ??
      getString(axiosError.message),
    isNetworkError: !axiosError.response,
    isCancelled: axios.isCancel(error) || axiosError.code === 'ERR_CANCELED',
    method: getString(axiosError.config?.method)?.toUpperCase(),
    url: getString(axiosError.config?.url),
  }
}

export const normalizeApiError = (
  error: unknown,
  fallbackMessage = GENERIC_ERROR_MESSAGE,
): NormalizedApiError => {
  if (isNormalizedApiError(error)) {
    return error.message === GENERIC_ERROR_MESSAGE
      ? { ...error, message: fallbackMessage }
      : error
  }

  const axiosDetails = getAxiosDetails(error)
  if (axiosDetails) {
    return {
      statusCode: axiosDetails.statusCode,
      errorCode: axiosDetails.errorCode,
      message: axiosDetails.message ?? fallbackMessage,
      errors: axiosDetails.errors,
      details: axiosDetails.details,
      traceId: axiosDetails.traceId,
      isNetworkError: axiosDetails.isNetworkError,
      isCancelled: axiosDetails.isCancelled,
      method: axiosDetails.method,
      url: axiosDetails.url,
      originalError: error,
    }
  }

  const body = parseJsonRecord(error)
  const message =
    getString(body?.message) ??
    getString(body?.Message) ??
    getString(body?.title) ??
    getString(body?.error) ??
    getString(error)

  return {
    statusCode:
      getNumber(body?.statusCode) ?? getNumber(body?.StatusCode),
    errorCode: getNumber(body?.errorCode) ?? getNumber(body?.ErrorCode),
    message: message ?? fallbackMessage,
    errors: getValidationErrors(body?.errors ?? body?.Errors),
    details: body?.details ?? body?.Details,
    traceId: getString(body?.traceId) ?? getString(body?.TraceId),
    isNetworkError: false,
    isCancelled: false,
    originalError: error,
  }
}

export const isNormalizedApiError = (
  error: unknown,
): error is NormalizedApiError =>
  isRecord(error) &&
  typeof error.message === 'string' &&
  typeof error.isNetworkError === 'boolean' &&
  typeof error.isCancelled === 'boolean' &&
  'originalError' in error

export const getFieldValidationMessages = (
  error: unknown,
  field: string,
): string[] => normalizeApiError(error).errors?.[field] ?? []

export const getValidationSummary = (
  error: unknown,
  fallbackMessage?: string,
): string => {
  const normalized = normalizeApiError(error, fallbackMessage)
  const firstValidationMessage = Object.values(normalized.errors ?? {}).find(
    (messages) => messages.length,
  )?.[0]
  return firstValidationMessage ?? normalized.message
}

export const normalizeGraphQLError = (
  error: GraphQLErrorLike,
  fallbackMessage?: string,
): NormalizedApiError => {
  const extensions = isRecord(error.extensions) ? error.extensions : {}
  const nestedError = isRecord(extensions.error) ? extensions.error : {}

  return normalizeApiError(
    {
      ...nestedError,
      statusCode:
        getNumber(extensions.statusCode) ?? getNumber(nestedError.statusCode),
      errorCode:
        getNumber(extensions.errorCode) ?? getNumber(nestedError.errorCode),
      traceId: getString(extensions.traceId) ?? getString(nestedError.traceId),
      message: getString(error.message) ?? getString(nestedError.message),
      errors: extensions.errors ?? nestedError.errors,
    },
    fallbackMessage,
  )
}

export const normalizeSignalRError = (
  error: unknown,
  fallbackMessage?: string,
): NormalizedApiError => {
  const errorMessage =
    isRecord(error) && typeof error.message === 'string'
      ? error.message
      : getString(error)
  const serializedPayload = errorMessage?.match(/\{[\s\S]*\}/)?.[0]

  if (serializedPayload) {
    const parsed = parseJsonRecord(serializedPayload)
    if (parsed) return normalizeApiError(parsed, fallbackMessage)
  }

  return normalizeApiError(errorMessage ?? error, fallbackMessage)
}

